/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import {
  type YMMSelectorOptions,
  type BrandSelectorEnabled,
  type LocationFilterOptions,
} from '../interface/models';
import { mapToIndividualBrandUrl, isTaxonomyPathMatching } from './isTaxonomyPathMatching';
import { matchesSegment } from './matchesSegment';
import { isSegmentable } from './typeGuards/isSegmentable';
import { isBrandSelectorEnabled } from './typeGuards/isBrandSelectorEnabled';
import { isFilterableByTaxonomy } from './typeGuards/isFilterableByTaxonomy';
import { isFilterableByYMM } from './typeGuards/isFilterableByYMM';
import { isFilterableByLocation } from './typeGuards/isFilterableByLocation';
import { isYMMSelectorMatching } from './isYMMSelectorMatching';
import { isLocationFilterMatching } from './isLocationFilterMatching';

const isBrandSelectorMatching = (cmsItem: BrandSelectorEnabled, brandName: string) => {
  const cmsItemBrandNames = cmsItem.brand_selector.map((brandSelector) => brandSelector.brandName);
  return cmsItemBrandNames.some((cmsItemBrandName) => cmsItemBrandName === brandName);
};

/**
 * Skip if YMM filtering is specified (make exists) but the entry doesn't support YMM or doesn't match criteria
 */
const shouldSkipForYMM = <T>(entry: T, ymmSelectorOptions: YMMSelectorOptions): boolean => {
  return (
    !!ymmSelectorOptions.make &&
    (!isFilterableByYMM(entry) || !isYMMSelectorMatching(entry, ymmSelectorOptions))
  );
};

type DeepFilterAndSegmentOptions<T> = {
  item: T | undefined;
  taxonomyPath: string;
  userSegments: string[];
  brandName?: string;
  ymmSelectorOptions?: YMMSelectorOptions;
  locationFilterOptions?: LocationFilterOptions;
  exactMatch?: boolean;
};

export const deepFilterAndSegmentContent = <T>({
  item,
  taxonomyPath,
  userSegments,
  brandName,
  ymmSelectorOptions = { make: null, model: null, year: null },
  locationFilterOptions,
  exactMatch = false,
}: DeepFilterAndSegmentOptions<T>): T | undefined => {
  if (item === undefined) {
    return undefined;
  }

  if (Array.isArray(item)) {
    const filteredArray = item.reduce<T[]>((acc, entry) => {
      // Check taxonomy filtering
      if (isFilterableByTaxonomy(entry)) {
        if (shouldSkipForYMM(entry, ymmSelectorOptions)) {
          return acc;
        }

        const pageTaxonomyPaths = mapToIndividualBrandUrl(taxonomyPath);
        const isMatchingTaxonomy = isTaxonomyPathMatching(entry, pageTaxonomyPaths, exactMatch);
        if (!isMatchingTaxonomy) {
          return acc;
        }
      }

      // Check segmentation filtering
      if (isSegmentable(entry)) {
        const isMatchingUserSegments = matchesSegment(userSegments, entry.segmentation.segment);
        if (!isMatchingUserSegments) {
          return acc;
        }
      }

      // Check brand filtering
      if (isBrandSelectorEnabled(entry)) {
        const isMatchingBrandSelector = isBrandSelectorMatching(entry, brandName ?? '');

        if (!isMatchingBrandSelector) {
          return acc;
        }
      }

      // Check YMM filtering
      if (isFilterableByYMM(entry)) {
        const isMatchingYMM = isYMMSelectorMatching(entry, ymmSelectorOptions);
        if (!isMatchingYMM) {
          return acc;
        }
      }

      // Check location filtering
      if (isFilterableByLocation(entry) && locationFilterOptions) {
        const isMatchingLocation = isLocationFilterMatching(entry, locationFilterOptions);
        if (!isMatchingLocation) {
          return acc;
        }
      }

      const updatedEntry = deepFilterAndSegmentContent({
        item: entry as unknown,
        taxonomyPath,
        userSegments,
        exactMatch,
        ymmSelectorOptions,
        brandName,
        locationFilterOptions,
      });

      if (updatedEntry !== undefined) {
        acc.push(updatedEntry as T);
      }

      return acc;
    }, []);

    return filteredArray as T;
  }

  if (typeof item === 'object' && item !== null) {
    // Check taxonomy filtering
    if (isFilterableByTaxonomy(item)) {
      if (shouldSkipForYMM(item, ymmSelectorOptions)) {
        return undefined;
      }

      const pageTaxonomyPaths = mapToIndividualBrandUrl(taxonomyPath);
      if (!isTaxonomyPathMatching(item, pageTaxonomyPaths, exactMatch)) {
        return undefined;
      }
    }

    // Check segmentation filtering
    if (isSegmentable(item)) {
      if (!matchesSegment(userSegments, item.segmentation.segment)) {
        return undefined;
      }
    }

    // Check brand filtering
    if (isBrandSelectorEnabled(item)) {
      const isMatchingBrandSelector = isBrandSelectorMatching(item, brandName ?? '');

      if (!isMatchingBrandSelector) {
        return undefined;
      }
    }

    // Check YMM filtering
    if (isFilterableByYMM(item)) {
      const isMatchingYMM = isYMMSelectorMatching(item, ymmSelectorOptions);
      if (!isMatchingYMM) {
        return undefined;
      }
    }

    // Check location filtering
    if (isFilterableByLocation(item) && locationFilterOptions) {
      const isMatchingLocation = isLocationFilterMatching(item, locationFilterOptions);
      if (!isMatchingLocation) {
        return undefined;
      }
    }

    const filteredObject = Object.entries(item).reduce<Record<string, unknown>>(
      (acc, [key, value]) => {
        if (
          key !== 'taxonomy_selector' &&
          key !== 'taxonomy' &&
          key !== 'segmentation' &&
          key !== 'segment' &&
          key !== 'brand_filter' &&
          key !== 'brand_selector' &&
          key !== 'ymm_filter' &&
          key !== 'ymm_selector' &&
          key !== 'location_filter_enabled' &&
          key !== 'location_filter'
        ) {
          const nestedValue = deepFilterAndSegmentContent({
            item: value as unknown,
            taxonomyPath,
            userSegments,
            exactMatch,
            ymmSelectorOptions,
            brandName,
            locationFilterOptions,
          });

          if (nestedValue !== undefined) {
            acc[key] = nestedValue;
          }
        } else {
          acc[key] = value;
        }

        return acc;
      },
      {},
    );

    return filteredObject as T;
  }

  return item;
};
